import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { User, Lock, FileText, ArrowLeft, Loader2, CheckCircle2, Clock, AlertCircle, Phone, MapPin, School, X, GraduationCap, MessageCircle } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';

const GOVERNORATES = ["الدقهلية", "القاهرة", "الجيزة", "الإسكندرية", "القليوبية", "الشرقية", "المنوفية", "الغربية", "دمياط", "كفر الشيخ", "البحيرة", "الإسماعيلية", "بورسعيد", "السويس", "مطروح", "شمال سيناء", "جنوب سيناء", "بني سويف", "الفيوم", "المنيا", "أسيوط", "سوهاج", "قنا", "الأقصر", "أسوان", "الوادي الجديد", "البحر الأحمر"];

const STUDY_BRANCHES = [
  { value: 'scientific', label: 'علمي' },
  { value: 'literary', label: 'أدبي' }
];

export const Login = () => {
  const location = useLocation();
  const initialTab = new URLSearchParams(location.search).get('tab');
  const [isLogin, setIsLogin] = useState(initialTab !== 'register');
  const [loading, setLoading] = useState(false);
  const [statusMessage, setStatusMessage] = useState({ type: '', text: '', code: '' });
  const [showFloatingAlert, setShowFloatingAlert] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showForgotPasswordModal, setShowForgotPasswordModal] = useState(false);
  const [showDeviceSwitchModal, setShowDeviceSwitchModal] = useState(false);
  const [pendingLoginData, setPendingLoginData] = useState(null);
  
  const [phoneCode, setPhoneCode] = useState('+20');
  const [fatherPhoneCode, setFatherPhoneCode] = useState('+20');
  const [motherPhoneCode, setMotherPhoneCode] = useState('+20');
  const [nameError, setNameError] = useState('');

  const COUNTRY_CODES = [
    { code: '+20', label: 'جمهورية مصر العربية (+20)' },
    { code: '+966', label: 'المملكة العربية السعودية (+966)' },
    { code: '+971', label: 'الإمارات العربية المتحدة (+971)' },
    { code: '+965', label: 'دولة الكويت (+965)' },
  ];

  const { signIn, register, user, profile } = useAuth();
  const navigate = useNavigate();

  // Device ID generation
  const getDeviceId = () => {
    let id = localStorage.getItem('sabeel_device_id');
    if (!id) {
      id = crypto.randomUUID ? crypto.randomUUID() : Math.random().toString(36).substring(2, 15);
      localStorage.setItem('sabeel_device_id', id);
    }
    return id;
  };

  const validateName = (name) => {
    const arabicRegex = /^[\u0600-\u06FF\s]+$/;
    if (!arabicRegex.test(name)) {
      return 'يرجى كتابة الاسم باللغة العربية فقط';
    }
    return '';
  };
  
  const [formData, setFormData] = useState({
    fullName: '',
    phone: '',
    password: '',
    bio: '',
    school: '',
    father_phone: '',
    mother_phone: '',
    governorate: '',
    grade: '2sec',
    study_branch: 'scientific'
  });

  // Wipe old session keys ONCE on Login page mount so the login form is clean,
  // but keep the device_id so it stays consistent.
  useEffect(() => {
    const deviceId = localStorage.getItem('sabeel_device_id');
    localStorage.removeItem('token');
    localStorage.removeItem('userId');
    if (deviceId) {
      localStorage.setItem('sabeel_device_id', deviceId);
    }
  }, []);

  // No auto-redirect effect here — after a successful login we do a full
  // window.location navigation to the correct dashboard, which is more reliable
  // and avoids redirect loops.

  const handleSubmit = async (e) => {
    if (e) e.preventDefault(); 
    setLoading(true);
    setStatusMessage({ type: '', text: '', code: '' });
    setShowFloatingAlert(false);
    setNameError('');

    if (!isLogin) {
      const currentNameError = validateName(formData.fullName);
      if (currentNameError) {
        setStatusMessage({ type: 'error', text: currentNameError });
        setLoading(false);
        return;
      }
    }

    if (!isLogin && /[0-9]/.test(formData.fullName)) {
      setStatusMessage({ type: 'error', text: 'لا يُسمح بالأرقام في الاسم. يرجى كتابة الاسم بالعربية.' });
      setLoading(false);
      return;
    }

    if (!isLogin && formData.fullName.length > 30) {
      setStatusMessage({ type: 'error', text: 'الاسم طويل جداً، يرجى الاختصار لـ 30 حرف.' });
      setLoading(false);
      return;
    }
    
    try {
      if (isLogin) {
        if (!formData.phone || !formData.password) {
          setStatusMessage({ type: 'error', text: 'يرجى إدخال رقم الموبايل وكلمة المرور.' });
          setLoading(false);
          return;
        }
        const result = await signIn(formData.phone.trim(), formData.password, getDeviceId());
        if (result.success) {
          const role = result?.profile?.role || 'student';
          const home = (role === 'admin' || role === 'boss') ? '/admin' : '/student';
          window.location.href = home;
          return;
        }
        
        const errStr = (result?.error || '').toString().toLowerCase();
        if (result?.error === 'ACCOUNT_KICKED') {
          setStatusMessage({ type: 'error', text: result.message || 'تم طردك من المنصة نهائياً.' });
        } else if (result?.error === 'DEVICE_LOCKED') {
          // Show device switch confirmation modal
          setPendingLoginData({ phone: formData.phone.trim(), password: formData.password, deviceId: getDeviceId() });
          setShowDeviceSwitchModal(true);
        } else if (errStr.includes('account_pending') || errStr.includes('مراجعة') || (result?.error !== 'ACCOUNT_NOT_FOUND' && result?.status === 403)) {
          setStatusMessage({ type: 'pending', text: 'حسابك في مرحلة المراجعة.. سنقوم بتفعيله في أقرب وقت.' });
        } else {
          // أي مشكلة تانية في الدخول (الباسورد غلط، الحساب مش موجود) نطلع رسالة روح اعمل حساب
          setShowFloatingAlert(true);
        }
        
      } else {
        if (!formData.fullName.trim() || !formData.phone.trim() || !formData.password.trim() || !formData.governorate || !formData.grade || !formData.study_branch) {
          setStatusMessage({ type: 'error', text: 'يرجى إكمال جميع البيانات الأساسية.' });
          setLoading(false);
          return;
        }

        if (!formData.father_phone.trim() && !formData.mother_phone.trim()) {
          setStatusMessage({ type: 'error', text: 'لا بد من إدخال رقم ولي أمر واحد على الأقل.' });
          setLoading(false);
          return;
        }

        setShowConfirmModal(true);
        setLoading(false);
        return;
      }
    } catch (error) {
      // لو الباك إند رمى Error صريح بدل ما يرجع رسالة، هنصطاده هنا
      if (isLogin) {
        setShowFloatingAlert(true);
      } else {
        setStatusMessage({ type: 'error', text: 'حدث خطأ في الاتصال، يرجى المحاولة مرة أخرى.' });
      }
    } finally { setLoading(false); }
  };

  const confirmRegistration = async () => {
    if (loading) return;
    setLoading(true);
    setStatusMessage({ type: '', text: '', code: '' });

    const formattedFatherPhone = formData.father_phone.trim() ? `(${fatherPhoneCode}) ${formData.father_phone.trim()}` : '';
    const formattedMotherPhone = formData.mother_phone.trim() ? `(${motherPhoneCode}) ${formData.mother_phone.trim()}` : '';
    const studentEmail = `${formData.phone.trim().replace(/\s/g, '')}@sabeel.com`;

    const nameParts = formData.fullName.trim().split(/\s+/);
    const fName = nameParts[0] || '';
    const mName = nameParts.length > 2 ? nameParts.slice(1, -1).join(' ') : '';
    const lName = nameParts.length > 1 ? nameParts[nameParts.length - 1] : 'طالب';

    try {
      const result = await register(
        studentEmail,
        formData.password,
        formData.fullName,
        {
          firstName: fName,
          middleName: mName,
          lastName: lName,
          bio: formData.bio || '',
          school: formData.school || '',
          phone: formData.phone.trim(),
          father_phone: formattedFatherPhone,
          mother_phone: formattedMotherPhone,
          governorate: formData.governorate,
          grade: formData.grade,
          study_branch: formData.study_branch,
          device_id: getDeviceId()
        }
      );

      if (result.success) {
        setShowConfirmModal(false);
        setIsLogin(true);
        setStatusMessage({ type: 'success', text: 'تم إرسال بياناتك بنجاح! سيتم مراجعة حسابك وتفعيله في أقرب وقت.' });
      } else {
        setStatusMessage({ type: 'error', text: result.error || 'فشل التسجيل، الرقم مستخدم مسبقاً.' });
        setShowConfirmModal(false);
      }
    } catch (error) {
      setStatusMessage({ type: 'error', text: 'حصلت مشكلة فنية، حاول تاني.' });
      setShowConfirmModal(false);
    } finally {
      setLoading(false);
    }
  };

  const confirmDeviceSwitch = async () => {
    if (loading || !pendingLoginData) return;
    setLoading(true);
    setStatusMessage({ type: '', text: '', code: '' });
    setShowDeviceSwitchModal(false);

    try {
      const result = await signIn(pendingLoginData.phone, pendingLoginData.password, pendingLoginData.deviceId, true);
      if (result.success) {
        const role = result?.profile?.role || 'student';
        const home = (role === 'admin' || role === 'boss') ? '/admin' : '/student';
        window.location.href = home;
        return;
      }
      setStatusMessage({ type: 'error', text: 'فشل في تبديل الجهاز، يرجى المحاولة مرة أخرى أو التواصل مع الدعم الفني.' });
    } catch (error) {
      setStatusMessage({ type: 'error', text: 'حدث خطأ في الاتصال، يرجى المحاولة مرة أخرى.' });
    } finally {
      setLoading(false);
      setPendingLoginData(null);
    }
  };

  // Shared input class
  const inputCls = "w-full bg-white/[0.05] border border-white/15 rounded-2xl py-4 pr-12 pl-4 text-sm focus:border-white/40 outline-none text-right text-white placeholder:text-zinc-500 transition shadow-inner";
  const inputClsNoIcon = "w-full bg-white/[0.05] border border-white/15 rounded-2xl py-4 px-4 text-sm focus:border-white/40 outline-none text-right text-white placeholder:text-zinc-500 transition shadow-inner";
  const selectCls = "w-full bg-white/[0.05] border border-white/15 rounded-2xl py-4 pr-12 pl-4 text-xs focus:border-white/40 outline-none text-right appearance-none text-white shadow-inner";
  const iconCls = "absolute right-4 top-1/2 -translate-y-1/2 text-white/40";

  return (
    <div 
      className="min-h-screen flex items-center justify-center p-4 md:p-8" 
      dir="rtl"
      style={{
        backgroundImage: 'url(/images/desert-dark.png)',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundAttachment: 'fixed',
      }}
    >
      {/* Dark overlay */}
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm z-0"></div>

      {/* ====== Custom CSS Animations ====== */}
      <style>{`
        .font-cairo {
          font-family: 'Cairo', sans-serif;
        }
        
        @keyframes sweepReveal {
          0% { clip-path: polygon(100% 0, 100% 0, 100% 100%, 100% 100%); opacity: 0; }
          20% { opacity: 1; }
          100% { clip-path: polygon(0 0, 100% 0, 100% 100%, 0 100%); opacity: 1; }
        }
        .animate-sweep {
          animation: sweepReveal 2.5s cubic-bezier(0.25, 1, 0.5, 1) forwards;
        }

        @keyframes floatWord {
          0% { transform: translateY(50px) scale(0.8); opacity: 0; }
          15% { opacity: 0.15; }
          85% { opacity: 0.15; }
          100% { transform: translateY(-150px) scale(1.2); opacity: 0; }
        }
        .floating-word {
          position: absolute;
          color: rgba(255,255,255,0.3);
          font-weight: bold;
          pointer-events: none;
          user-select: none;
          opacity: 0;
          animation: floatWord 10s ease-in-out infinite;
          font-family: 'Cairo', sans-serif;
        }
      `}</style>
      
      {/* ====== Confirmation Modal ====== */}
      {showConfirmModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/80 backdrop-blur-md" onClick={() => setShowConfirmModal(false)}></div>
          <div className="relative rounded-3xl p-6 w-full max-w-md shadow-[0_20px_50px_rgba(0,0,0,0.5)]" style={{ background: 'rgba(0,0,0,0.85)', backdropFilter: 'blur(20px)', border: '1px solid rgba(255,255,255,0.15)' }}>
            <h3 className="text-xl font-black mb-4 text-center text-white font-cairo">تأكيد البيانات</h3>
            <div className="bg-white/5 rounded-2xl p-4 mb-6 space-y-3 text-sm border border-white/10 text-white font-cairo">
              <div className="flex justify-between border-b border-white/5 pb-2"><span className="text-gray-400">الاسم:</span><span className="font-bold">{formData.fullName}</span></div>
              <div className="flex justify-between border-b border-white/5 pb-2"><span className="text-gray-400">الموبايل:</span><span className="font-bold text-left" dir="ltr">{phoneCode} {formData.phone}</span></div>
              <div className="flex justify-between border-b border-white/5 pb-2"><span className="text-gray-400">الصف:</span><span className="font-bold">{formData.grade === '1sec' ? 'أولى ثانوي' : formData.grade === '2sec' ? 'تانية ثانوي' : 'تالتة ثانوي'}</span></div>
              <div className="flex justify-between border-b border-white/5 pb-2"><span className="text-gray-400">الشعبة:</span><span className="font-bold">{formData.study_branch === 'scientific' ? 'علمي' : 'أدبي'}</span></div>
              <div className="flex justify-between border-b border-white/5 pb-2"><span className="text-gray-400">موبايل الأب:</span><span className="font-bold text-left" dir="ltr">{fatherPhoneCode} {formData.father_phone}</span></div>
            </div>
            <div className="flex gap-3 font-cairo">
              <button onClick={confirmRegistration} disabled={loading} className="flex-1 bg-white hover:bg-gray-200 text-black font-black py-3 px-4 rounded-xl transition flex items-center justify-center shadow-lg">
                {loading ? <Loader2 className="animate-spin" size={20} /> : 'تأكيد'}
              </button>
              <button onClick={() => setShowConfirmModal(false)} disabled={loading} className="flex-1 bg-white/10 hover:bg-white/20 text-white font-bold py-3 px-4 rounded-xl transition">
                مراجعة
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ====== Floating 'No Account' Toast Alert ====== */}
      {showFloatingAlert && (
        <div className="fixed top-6 left-1/2 z-50 w-[92%] max-w-md -translate-x-1/2 animate-slide-up">
          <div className="relative border border-white/20 rounded-2xl p-5 shadow-[0_10px_40px_rgba(0,0,0,0.5)] backdrop-blur-xl font-cairo" style={{ background: 'rgba(0,0,0,0.9)' }}>
            <button onClick={() => setShowFloatingAlert(false)} className="absolute top-3 left-3 text-white/40 hover:text-white transition-colors">
              <X size={18} />
            </button>
            <div className="flex items-start gap-3">
              <AlertCircle className="text-white shrink-0 mt-0.5 animate-pulse" size={26} />
              <div className="space-y-3">
                <p className="text-sm font-bold leading-relaxed text-white">عفواً يا بطل، الرقم أو الباسورد غلط، أو الرقم ده ملوش حساب عندنا.. لو لسه مسجلتش، اعمل حساب جديد عشان تنورنا.</p>
                <button onClick={() => { setShowFloatingAlert(false); setIsLogin(false); }} className="w-full bg-white hover:bg-gray-200 text-black font-black text-xs py-3 px-6 rounded-xl transition-all flex items-center justify-center gap-2 shadow-lg">
                  <User size={16} /> إنشاء حساب جديد
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Main Glass Container (Split Layout) */}
      <div className="relative z-10 w-full max-w-6xl flex flex-col md:flex-row rounded-[3rem] overflow-hidden border border-white/10 shadow-[0_30px_90px_rgba(0,0,0,0.8)]">
        
        {/* Right Side - Image (Instructor) & Floating Arabic Art */}
        <div className="hidden md:flex md:w-1/2 relative items-end justify-center border-l border-white/5 overflow-hidden" style={{ background: 'rgba(0,0,0,0.4)' }}>
          
          {/* Floating Arabic Letters */}
          <div className="absolute inset-0 z-0 overflow-hidden pointer-events-none opacity-80">
            <span className="floating-word text-3xl" style={{top: '5%', left: '5%', animationDelay: '0s'}}>نحو</span>
            <span className="floating-word text-8xl opacity-10" style={{top: '10%', right: '10%', animationDelay: '2s'}}>ض</span>
            <span className="floating-word text-2xl" style={{top: '25%', left: '20%', animationDelay: '1s'}}>بلاغة</span>
            <span className="floating-word text-6xl opacity-10" style={{top: '35%', right: '25%', animationDelay: '3s'}}>قاف</span>
            <span className="floating-word text-5xl" style={{top: '45%', left: '10%', animationDelay: '0.5s'}}>أدب</span>
            <span className="floating-word text-8xl opacity-10" style={{top: '55%', right: '5%', animationDelay: '4s'}}>ع</span>
            <span className="floating-word text-xl" style={{top: '65%', left: '30%', animationDelay: '1.5s'}}>قراءة</span>
            <span className="floating-word text-4xl" style={{top: '75%', right: '20%', animationDelay: '3.5s'}}>شعر</span>
            <span className="floating-word text-7xl opacity-10" style={{top: '85%', left: '5%', animationDelay: '2.5s'}}>ن</span>
            <span className="floating-word text-2xl" style={{top: '90%', right: '35%', animationDelay: '0.2s'}}>خط</span>
            <span className="floating-word text-6xl" style={{top: '15%', left: '40%', animationDelay: '0s'}}>ا</span>
            <span className="floating-word text-5xl" style={{top: '50%', right: '40%', animationDelay: '1s'}}>ل</span>
            <span className="floating-word text-9xl opacity-10" style={{top: '20%', right: '40%', animationDelay: '2s'}}>م</span>
            <span className="floating-word text-xl" style={{top: '30%', left: '45%', animationDelay: '3s'}}>نص</span>
            <span className="floating-word text-4xl" style={{top: '70%', left: '45%', animationDelay: '4s'}}>فكر</span>
          </div>

          {/* Glow behind instructor */}
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-white/5 rounded-full blur-[120px] z-0"></div>
          
          <div className="absolute top-12 right-12 z-20 text-right">
            <div className="animate-sweep opacity-0">
              <h2 className="text-5xl md:text-6xl font-black text-white mb-1 drop-shadow-[0_5px_15px_rgba(0,0,0,0.8)]">
                سبيل <span className="text-gray-300">التفوق</span>
              </h2>
              <h3 className="text-3xl font-bold text-gray-300 mt-2 font-cairo">الأستاذ محمد حامد</h3>
            </div>
          </div>

          <img 
            src="/images/1.png" 
            alt="الأستاذ محمد حامد" 
            className="relative z-10 w-[90%] max-w-md object-contain object-bottom drop-shadow-[0_20px_40px_rgba(0,0,0,0.9)]"
            style={{ maxHeight: '85%', filter: 'grayscale(15%)' }}
          />
        </div>

        {/* Left Side - Frosted Glass Form */}
        <div className="w-full md:w-1/2 p-8 md:p-14 relative overflow-hidden font-cairo flex flex-col justify-center" style={{ background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(30px)', WebkitBackdropFilter: 'blur(30px)' }}>

          <div className="md:hidden text-center mb-8 relative z-10">
            <div className="w-16 h-16 bg-white rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg">
              <span className="text-3xl font-black text-black font-sans">♞</span>
            </div>
            <h1 className="text-4xl font-black text-white mb-1">
              سبيل <span className="text-gray-300">التفوق</span>
            </h1>
          </div>

          {/* Login/Register Toggle */}
          <div className="flex bg-white/5 p-1.5 rounded-2xl mb-8 border border-white/5 relative z-10 shadow-inner shrink-0">
            <button type="button" onClick={() => { setIsLogin(true); setNameError(''); setStatusMessage({ type: '', text: '', code: '' }); }} className={`flex-1 py-3.5 rounded-xl text-sm font-black transition-all ${isLogin ? 'bg-white text-black shadow-lg scale-[1.03]' : 'text-zinc-400 hover:text-white'}`}>
              دخول الطلاب
            </button>
            <button type="button" onClick={() => { setIsLogin(false); setNameError(''); setStatusMessage({ type: '', text: '', code: '' }); }} className={`flex-1 py-3.5 rounded-xl text-sm font-black transition-all ${!isLogin ? 'bg-white text-black shadow-lg scale-[1.03]' : 'text-zinc-400 hover:text-white'}`}>
              إنشاء حساب
            </button>
          </div>

          {/* Status Messages */}
          {statusMessage.text && (
            <div className={`relative z-10 mb-6 p-4 rounded-2xl border flex items-start gap-3 shadow-lg animate-fade-in shrink-0 ${
              statusMessage.type === 'success' ? 'bg-white/10 border-white/20 text-white' :
              statusMessage.type === 'pending' ? 'bg-white/10 border-white/20 text-gray-300' : 
              'bg-white/10 border-white/20 text-gray-300'
            }`}>
              {statusMessage.type === 'success' ? <CheckCircle2 size={18} className="shrink-0 mt-0.5" /> : 
               statusMessage.type === 'pending' ? <Clock size={18} className="shrink-0 mt-0.5" /> : <AlertCircle size={18} className="shrink-0 mt-0.5" />}
              <p className="text-xs font-bold leading-relaxed">{statusMessage.text}</p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4 max-h-[60vh] md:max-h-[500px] overflow-y-auto pr-3 custom-scrollbar relative z-10 scroll-smooth pb-4">
            
            {!isLogin && (
              <div className="relative group">
                <User className={iconCls} size={18} />
                <input type="text" placeholder="الاسم الرباعي (باللغة العربية)" required maxLength={30} className={inputCls} value={formData.fullName} onChange={(e) => {
                  const value = e.target.value;
                  setFormData({...formData, fullName: value});
                  setNameError(validateName(value));
                }} />
                {nameError && <p className="text-red-400 text-xs mt-1 font-bold">{nameError}</p>}
              </div>
            )}

            {/* Phone */}
            <div className="relative group flex flex-col gap-3">
              <div className="relative w-full">
                <MapPin className={iconCls} size={17} />
                <select className={selectCls} value={phoneCode} onChange={(e) => setPhoneCode(e.target.value)} dir="rtl">
                  {COUNTRY_CODES.map(c => <option key={c.code} value={c.code} className="bg-black">{c.label}</option>)}
                </select>
                <div className="absolute left-4 top-1/2 -translate-y-1/2 pointer-events-none text-white/40 text-[10px]">▼</div>
              </div>
              <div className="relative w-full">
                <Phone className={iconCls} size={18} />
                <input type="tel" maxLength={20} placeholder="رقم الموبايل" required className="w-full bg-white/[0.05] border border-white/15 rounded-2xl py-4 pr-12 pl-4 text-sm focus:border-white/40 outline-none text-left text-white placeholder:text-zinc-500 transition shadow-inner" value={formData.phone} onChange={(e) => setFormData({...formData, phone: e.target.value})} dir="ltr" />
              </div>
            </div>

            {!isLogin && (
              <>
                <div className="grid grid-cols-2 gap-4">
                  <div className="relative">
                    <FileText className={iconCls} size={17} />
                    <select required className={selectCls} value={formData.grade} onChange={(e) => setFormData({ ...formData, grade: e.target.value })}>
                      <option value="1sec" className="bg-black">أولى ثانوي</option>
                      <option value="2sec" className="bg-black">تانية ثانوي</option>
                      <option value="3sec" className="bg-black">تالتة ثانوي</option>
                    </select>
                  </div>
                  <div className="relative">
                    <GraduationCap className={iconCls} size={17} />
                    <select required className={selectCls} value={formData.study_branch} onChange={(e) => setFormData({ ...formData, study_branch: e.target.value })}>
                      {STUDY_BRANCHES.map(branch => <option key={branch.value} value={branch.value} className="bg-black">{branch.label}</option>)}
                    </select>
                  </div>
                </div>

                {/* Parent Phones */}
                <p className="text-xs text-gray-300 font-bold mb-2 bg-white/5 p-2 rounded border border-white/10">
                  ملاحظة: في حال عدم وجود رقم لأحد الأب أو الأم يرجى ترك المكان فاضي.
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-1">
                  <div className="relative group flex flex-col gap-2.5">
                    <select className="w-full bg-white/[0.05] border border-white/15 rounded-2xl py-4 pl-6 pr-1 text-xs focus:border-white/40 outline-none appearance-none text-white text-right shadow-inner" value={fatherPhoneCode} onChange={(e) => setFatherPhoneCode(e.target.value)} dir="rtl">
                      {COUNTRY_CODES.map(c => <option key={c.code} value={c.code} className="bg-black">{c.code}</option>)}
                    </select>
                    <div className="relative flex-1">
                        <Phone className={iconCls} size={16} />
                        <input type="tel" maxLength={20} placeholder="موبايل الأب" required className="w-full bg-white/[0.05] border border-white/15 rounded-2xl py-4 pr-10 pl-4 text-sm focus:border-white/40 outline-none text-right text-white placeholder:text-zinc-500 shadow-inner" value={formData.father_phone} onChange={(e) => setFormData({...formData, father_phone: e.target.value})} dir="ltr"/>
                    </div>
                  </div>
                  <div className="relative group flex flex-col gap-2.5">
                    <select className="w-full bg-white/[0.05] border border-white/15 rounded-2xl py-4 pl-6 pr-1 text-xs focus:border-white/40 outline-none appearance-none text-white text-right shadow-inner" value={motherPhoneCode} onChange={(e) => setMotherPhoneCode(e.target.value)} dir="rtl">
                      {COUNTRY_CODES.map(c => <option key={c.code} value={c.code} className="bg-black">{c.code}</option>)}
                    </select>
                    <div className="relative flex-1">
                        <Phone className={iconCls} size={16} />
                        <input type="tel" maxLength={20} placeholder="موبايل الأم" className="w-full bg-white/[0.05] border border-white/15 rounded-2xl py-4 pr-10 pl-4 text-sm focus:border-white/40 outline-none text-right text-white placeholder:text-zinc-500 shadow-inner" value={formData.mother_phone} onChange={(e) => setFormData({...formData, mother_phone: e.target.value})} dir="ltr"/>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4 pt-1">
                  <div className="relative">
                    <MapPin className={iconCls} size={17} />
                    <select required className={selectCls} value={formData.governorate} onChange={(e) => setFormData({...formData, governorate: e.target.value})}>
                      <option value="" className="bg-black">المحافظة</option>
                      {GOVERNORATES.map(gov => <option key={gov} value={gov} className="bg-black">{gov}</option>)}
                    </select>
                  </div>
                  <div className="relative">
                    <School className={iconCls} size={17} />
                    <input type="text" placeholder="اسم المدرسة (اختياري)" className="w-full bg-white/[0.05] border border-white/15 rounded-2xl py-4 pr-12 pl-4 text-xs focus:border-white/40 outline-none text-right text-white placeholder:text-zinc-500 shadow-inner" value={formData.school} onChange={(e) => setFormData({...formData, school: e.target.value})} />
                  </div>
                </div>
              </>
            )}

            {/* Password */}
            <div className="relative group border-t border-white/5 pt-4">
              <Lock className={iconCls} size={18} />
              <input type="password" placeholder="كلمة المرور" required className={inputCls} value={formData.password} onChange={(e) => setFormData({...formData, password: e.target.value})} />
            </div>

            {/* Forgot Password Link */}
            {isLogin && (
              <button
                type="button"
                onClick={() => setShowForgotPasswordModal(true)}
                className="text-gray-400 hover:text-white text-sm font-bold transition block w-full text-center mt-1"
              >
                نسيت كلمة المرور؟
              </button>
            )}

            <button type="submit" disabled={loading} className="w-full bg-white hover:bg-gray-200 text-black font-black py-4.5 mt-6 rounded-2xl transition-all flex items-center justify-center gap-3 disabled:opacity-50 shadow-lg hover:-translate-y-0.5 active:scale-[0.98] shrink-0">
              {loading ? <Loader2 className="animate-spin" size={20} /> : (
                <><ArrowLeft size={20} /><span>{isLogin ? 'دخول آمن للمنصة' : 'إنشاء حساب جديد'}</span></>
              )}
            </button>
          </form>
        </div>
      </div>

      {/* ====== Forgot Password Modal ====== */}
      {showForgotPasswordModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={() => setShowForgotPasswordModal(false)}>
          <div className="absolute inset-0 bg-black/85 backdrop-blur-md"></div>
          <div className="relative rounded-3xl p-6 w-full max-w-md shadow-[0_20px_50px_rgba(0,0,0,0.5)] z-10" style={{ background: 'rgba(0,0,0,0.9)', backdropFilter: 'blur(20px)', border: '1px solid rgba(255,255,255,0.15)' }} onClick={e => e.stopPropagation()}>
            <button onClick={() => setShowForgotPasswordModal(false)} className="absolute top-4 left-4 text-white/40 hover:text-white transition-colors">
              <X size={20} />
            </button>
            
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-white rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg">
                <MessageCircle size={32} className="text-black" />
              </div>
              <h3 className="text-xl font-black text-white font-cairo mb-2">نسيت كلمة المرور؟</h3>
              <p className="text-sm text-gray-400 font-cairo">تواصل مع الدعم الفني عبر واتساب وسيتم مساعدتك في استعادة حسابك</p>
            </div>

            <div className="space-y-3">
              <a
                href="https://wa.me/201028608446?text=السلام عليكم، نسيت كلمة المرور الخاصة بحسابي على منصة سبيل التفوق، أرجو المساعدة."
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-3 w-full bg-white hover:bg-gray-200 text-black font-bold py-4 px-5 rounded-xl transition-all shadow-lg"
              >
                <MessageCircle size={22} className="shrink-0" />
                <span className="font-cairo text-sm">تواصل مع الدعم الفني (1)</span>
              </a>

              <a
                href="https://wa.me/201064756266?text=السلام عليكم، نسيت كلمة المرور الخاصة بحسابي على منصة سبيل التفوق، أرجو المساعدة."
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-3 w-full bg-white hover:bg-gray-200 text-black font-bold py-4 px-5 rounded-xl transition-all shadow-lg"
              >
                <MessageCircle size={22} className="shrink-0" />
                <span className="font-cairo text-sm">تواصل مع الدعم الفني (2)</span>
              </a>
            </div>

            <button
              onClick={() => setShowForgotPasswordModal(false)}
              className="w-full mt-4 py-3 bg-white/10 hover:bg-white/20 text-white font-bold rounded-xl transition"
            >
              إغلاق
            </button>
          </div>
        </div>
      )}

      {/* ====== Device Switch Confirmation Modal ====== */}
      {showDeviceSwitchModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={() => { setShowDeviceSwitchModal(false); setPendingLoginData(null); }}>
          <div className="absolute inset-0 bg-black/85 backdrop-blur-md"></div>
          <div className="relative rounded-3xl p-6 w-full max-w-md shadow-[0_20px_50px_rgba(0,0,0,0.5)] z-10" style={{ background: 'rgba(0,0,0,0.9)', backdropFilter: 'blur(20px)', border: '1px solid rgba(255,255,255,0.15)' }} onClick={e => e.stopPropagation()}>
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-white rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg">
                <svg className="w-8 h-8 text-black" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="2" y="3" width="20" height="14" rx="2" ry="2"></rect>
                  <line x1="8" y1="21" x2="16" y2="21"></line>
                  <line x1="12" y1="17" x2="12" y2="21"></line>
                </svg>
              </div>
              <h3 className="text-xl font-black text-white font-cairo mb-3">تبديل الجهاز</h3>
              <p className="text-sm text-gray-400 font-cairo leading-relaxed">
                هذا الحساب مسجل حالياً على جهاز آخر.
                <br />
                هل تريد تسجيل الخروج من الجهاز الآخر وربط الحساب بهذا الجهاز بدلاً من ذلك؟
              </p>
            </div>

            <div className="flex gap-3 font-cairo">
              <button
                onClick={confirmDeviceSwitch}
                disabled={loading}
                className="flex-1 bg-white hover:bg-gray-200 text-black font-black py-3 px-4 rounded-xl transition flex items-center justify-center shadow-lg disabled:opacity-50"
              >
                {loading ? <Loader2 className="animate-spin" size={20} /> : 'نعم، تبديل الجهاز'}
              </button>
              <button
                onClick={() => { setShowDeviceSwitchModal(false); setPendingLoginData(null); }}
                disabled={loading}
                className="flex-1 bg-white/10 hover:bg-white/20 text-white font-bold py-3 px-4 rounded-xl transition"
              >
                إلغاء
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
